<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-50 p-4" @click.self="close">
    <div class="bg-white rounded-xl shadow-xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden">
      <div class="px-5 py-4 border-b border-gray-200 flex items-start justify-between gap-3">
        <div class="min-w-0">
          <h2 class="text-lg font-bold text-gray-900 truncate">{{ title }}</h2>
          <p class="mt-1 text-sm text-gray-500">{{ subtitle }}</p>
        </div>
        <div class="flex items-center gap-2 flex-shrink-0">
          <button
            v-if="fileUrl"
            type="button"
            class="inline-flex items-center px-3 py-2 text-sm font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            @click="openInNewTab"
          >
            <i class="fa-solid fa-up-right-from-square mr-2"></i>
            Nueva pestaña
          </button>
          <button type="button" class="text-gray-400 hover:text-gray-700 px-2 py-1" @click="close">
            <i class="fa-solid fa-xmark text-lg"></i>
          </button>
        </div>
      </div>

      <div class="flex-1 min-h-0 bg-gray-100 relative overflow-auto" ref="pagesContainer">
        <div v-if="loading" class="absolute inset-0 flex items-center justify-center">
          <Loading />
        </div>
        <div v-else-if="error" class="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center">
          <p class="text-sm text-red-700">{{ error }}</p>
          <button
            type="button"
            class="inline-flex items-center px-4 py-2 text-sm font-semibold bg-indigo-700 text-white rounded-md hover:bg-indigo-800"
            @click="fetchDownloadUrl"
          >
            Reintentar
          </button>
        </div>
        <div v-else class="flex flex-col items-center gap-4 py-4">
          <canvas
            v-for="page in numPages"
            :key="page"
            :ref="setPageRef(page)"
            class="shadow-md bg-white max-w-full"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as pdfjsLib from 'pdfjs-dist';
import { markRaw } from 'vue';
import Loading from '@/components/commons/Loading.vue';
import { getLeaveDocumentBlob } from '@/service/leave.service';

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.BASE_URL}pdf.worker.min.mjs`;

export default {
  name: 'LeaveDocumentViewer',
  components: { Loading },
  props: {
    document: {
      type: Object,
      required: true,
    },
  },
  emits: ['close'],
  data() {
    return {
      loading: false,
      error: '',
      fileUrl: '',
      numPages: 0,
      pageRefs: {},
      pdfDoc: null,
    };
  },
  computed: {
    documentId() {
      return this.document.id || this.document.document_id;
    },
    title() {
      return this.document.file_name || this.document.name || this.document.original_name || 'Documento';
    },
    subtitle() {
      return this.document.document_type || this.document.type || 'General';
    },
  },
  created() {
    this.fetchDownloadUrl();
  },
  beforeUnmount() {
    this.revokeFileUrl();
    this.pdfDoc?.loadingTask?.destroy();
  },
  methods: {
    setPageRef(page) {
      return (el) => {
        if (el) this.pageRefs[page] = el;
      };
    },
    close() {
      this.revokeFileUrl();
      this.pdfDoc?.loadingTask?.destroy();
      this.$emit('close');
    },
    revokeFileUrl() {
      if (this.fileUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(this.fileUrl);
      }
      this.fileUrl = '';
    },
    openInNewTab() {
      if (this.fileUrl) window.open(this.fileUrl, '_blank', 'noopener');
    },
    async readErrorMessage(error) {
      const data = error?.response?.data;
      if (data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text());
          return parsed.message || parsed.error || '';
        } catch {
          return '';
        }
      }
      return data?.message || data?.error || error?.message || '';
    },
    async renderPdf(arrayBuffer) {
      this.pdfDoc = markRaw(await pdfjsLib.getDocument({ data: arrayBuffer }).promise);
      this.numPages = this.pdfDoc.numPages;
      this.loading = false;
      await this.$nextTick();

      const containerWidth = this.$refs.pagesContainer?.clientWidth || 800;

      for (let pageNumber = 1; pageNumber <= this.numPages; pageNumber += 1) {
        const canvas = this.pageRefs[pageNumber];
        if (!canvas) continue;
        const page = await this.pdfDoc.getPage(pageNumber);
        const unscaledViewport = page.getViewport({ scale: 1 });
        const scale = Math.min((containerWidth - 32) / unscaledViewport.width, 2);
        const viewport = page.getViewport({ scale });

        canvas.width = viewport.width;
        canvas.height = viewport.height;

        const context = canvas.getContext('2d');
        await page.render({ canvasContext: context, viewport }).promise;
      }
    },
    async fetchDownloadUrl() {
      if (!this.documentId) {
        this.error = 'No se pudo identificar el documento.';
        return;
      }
      this.loading = true;
      this.error = '';
      this.revokeFileUrl();
      this.pageRefs = {};
      this.numPages = 0;
      try {
        const blob = await getLeaveDocumentBlob(this.documentId);
        this.fileUrl = URL.createObjectURL(blob);
        const arrayBuffer = await blob.arrayBuffer();
        await this.renderPdf(arrayBuffer);
      } catch (error) {
        const status = error?.response?.status;
        const apiMessage = await this.readErrorMessage(error);
        if (status === 401) {
          this.error = 'Tu sesión expiró. Volvé a iniciar sesión.';
        } else if (status === 403) {
          this.error = 'No tenés acceso a este documento.';
        } else if (status === 404) {
          this.error = 'El documento no existe.';
        } else {
          this.error = apiMessage || 'No se pudo abrir el documento.';
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
